import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp, where, deleteField, startAt, endAt, limit } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firestoreProd, firestoreDev } from '../../firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TinyMCEEditor from '../../components/tinyMCE/TinyMCEEditor';
import Modal from 'react-bootstrap/Modal';
import { cloudRunApiUrl, apiUrl } from '../../globalVariables';
import GrapesEditor from '../../components/GrapesJS/GrapesEditor';

const { REACT_APP_ADMIN_TOKEN } = process.env;

// Hardcoded admin token as fallback (matches backend validation)
const ADMIN_TOKEN = REACT_APP_ADMIN_TOKEN || "928c6b5d-d111-4cc4-a275-61992e8d35c9";

// Email branding templates from Marketing page
const defaultBrandedFooter = `<table class="paragraph_block block-18" width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="mso-table-lspace: 0pt; mso-table-rspace: 0pt; word-break: break-word; border-top: 1px solid #dddddd;padding-bottom:30px;padding-left:10px;padding-right:10px;padding-top:30px;margin-top:30px;">
    <tr>
        <td class="pad" style="padding-bottom:30px;padding-left:10px;padding-right:10px;padding-top:10px;">
            <div style="color:#444a5b;direction:ltr;font-family:Arial, 'Helvetica Neue', Helvetica, sans-serif;font-size:12px;font-weight:400;letter-spacing:0px;line-height:120%;text-align:center;mso-line-height-alt:14.399999999999999px;">
                <p style="margin: 0; margin-bottom: 16px;">Addy AI, Inc.</p>
                <p style="margin: 0;">945 Market Street, Suite 501, San Francisco, CA 94103<br><br><a href="https://addy.so/email-preferences?uid={{uid}}" target="_blank" style="text-decoration: underline; color: #444a5b;" rel="noopener">Unsubscribe</a></p>
            </div>
        </td>
    </tr>
</table>`;

const defaultBrandedHeader = `<!-- Logo and Company Name (Horizontal) -->
<table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="text-align:center;">
    <tr>
        <td style="padding-top:10px; padding-bottom:30px;">
            <table align="left" border="0" cellpadding="0" cellspacing="0" role="presentation"
                    style="display: flex; align-items: center;">
                <tr style="display: flex; align-items: center;">
                    <td style="vertical-align:middle;">
                        <img src="https://i.imgur.com/gVdnANB.png" alt="Addy AI Logo"
                                style="height:24px; display:block;">
                    </td>
                    <td style="vertical-align:middle; padding-left:10px; font-family:Arial,Helvetica,sans-serif; color:#444a5b; font-size:19px; font-weight:bold;">
                        Addy AI
                    </td>
                </tr>
            </table>
        </td>
    </tr>
</table>`;

/**
 * Announcements Management Page
 * 
 * This page provides an admin interface for creating, editing, and managing announcements.
 * Features:
 * - Create announcements with rich text editor (Markdown output)
 * - Upload media (images/videos) to Firebase Storage
 * - Set announcement type (announcement/fix) and publication date
 * - View all announcements in a table
 * - Edit existing announcements
 * - Delete announcements
 */
/**
 * Display surfaces an announcement can render on. This is the authoring source
 * of truth — add a new surface here and it appears in the form automatically.
 * Keep the `value`s in sync with the backend KNOWN_SURFACES list.
 */
const DISPLAY_SURFACE_OPTIONS = [
    { value: 'banner', label: 'Banner', help: 'Full-width strip at the top of the app.' },
    { value: 'modal', label: 'Modal popup', help: 'Centered popup shown after users log in.' },
    { value: 'chat', label: 'Chat strip', help: 'Compact strip docked at the top of the Ask Addy chat box.' },
];

// Soft length guidance so the chat strip stays on a tidy single line.
const CHAT_TITLE_MAX = 80;
const LINK_TEXT_MAX = 24;

/**
 * Who an announcement is shown to. "all" is the historical behaviour and the
 * shape of every announcement written before audience targeting existed;
 * "organizations" limits it to members of the selected organizations.
 */
const AUDIENCE_ALL = 'all';
const AUDIENCE_ORGANIZATIONS = 'organizations';
const AUDIENCE_INTEGRATIONS = 'integrations';

// Backend preset key prefix for "everyone whose organization has <integration>
// connected" (models/Admin.js). Shared by the email audience picker.
const INTEGRATION_PRESET_PREFIX = 'integration-';

/**
 * Integrations an announcement can be targeted at, e.g. "everyone on Arive".
 * Mirrors INTEGRATION_CATALOG in the backend's
 * services/integrations/organizationIntegrationStatus.js, which decides who
 * actually matches — keep the IDs in sync when an integration is added there.
 */
const INTEGRATION_OPTIONS = [
    { id: 'encompass', name: 'Encompass LOS', type: 'LOS' },
    { id: 'meridianlink', name: 'MeridianLink LOS', type: 'LOS' },
    { id: 'lendingpad', name: 'LendingPad LOS', type: 'LOS' },
    { id: 'arive', name: 'Arive', type: 'LOS' },
    { id: 'finmo', name: 'Finmo', type: 'LOS' },
    { id: 'lenderprice', name: 'Lender Price', type: 'Pricing' },
    { id: 'extend', name: 'Extend OCR', type: 'OCR' },
    { id: 'docusign', name: 'DocuSign', type: 'E-sign' },
    { id: 'gohighlevel', name: 'GoHighLevel', type: 'CRM' },
    { id: 'salesforce', name: 'Salesforce', type: 'CRM' },
    { id: 'hubspot', name: 'HubSpot', type: 'CRM' },
    { id: 'bonzo', name: 'Bonzo', type: 'CRM' },
];

/** Display name for an integration ID, falling back to the raw ID. */
const integrationDisplayName = (id) =>
    INTEGRATION_OPTIONS.find((option) => option.id === id)?.name || id;

// Organizations returned per name-prefix search. There are ~20k orgs, so the
// picker is search-driven rather than a full list.
const ORG_SEARCH_LIMIT = 25;

/** Display name for an organization document, falling back to its ID. */
const orgDisplayName = (data, id) => data?.name || data?.organizationName || id;

/** Trim, drop blanks and de-duplicate a stored list of IDs. */
const normalizeIdList = (values) => {
    if (!Array.isArray(values)) return [];
    const ids = values
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean);
    return [...new Set(ids)];
};

/**
 * Resolve an announcement's audience. Announcements saved before targeting
 * existed have no `audience` field and are treated as all-users.
 */
const resolveAudienceFromAnnouncement = (announcement) => {
    const audience = announcement?.audience;
    if (audience?.type === AUDIENCE_ORGANIZATIONS) {
        return {
            type: AUDIENCE_ORGANIZATIONS,
            organizationIds: normalizeIdList(audience.organizationIds),
            integrations: []
        };
    }
    if (audience?.type === AUDIENCE_INTEGRATIONS) {
        return {
            type: AUDIENCE_INTEGRATIONS,
            organizationIds: [],
            integrations: normalizeIdList(audience.integrations)
        };
    }
    return { type: AUDIENCE_ALL, organizationIds: [], integrations: [] };
};

/**
 * Resolve an announcement's surfaces from either the new `displaySurfaces`
 * array or the legacy `displayMode` string ("banner" | "modal" | "both").
 */
const resolveSurfacesFromAnnouncement = (announcement) => {
    if (Array.isArray(announcement?.displaySurfaces) && announcement.displaySurfaces.length > 0) {
        return announcement.displaySurfaces;
    }
    const legacy = announcement?.displayMode;
    if (legacy === 'both') return ['banner', 'modal'];
    if (legacy) return [legacy];
    return ['banner'];
};

/** Derive a legacy displayMode value so older clients keep working during rollout. */
const toLegacyDisplayMode = (surfaces) => {
    if (surfaces.includes('banner') && surfaces.includes('modal')) return 'both';
    return surfaces[0] || 'banner';
};

/**
 * Convert a stored value (Firestore Timestamp | Date | ISO string) into the
 * `YYYY-MM-DDTHH:mm` local-time string required by <input type="datetime-local">.
 */
const toDatetimeLocal = (value) => {
    if (!value) return '';
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const offsetMs = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
};

/** Current local time formatted for a datetime-local input. */
const nowDatetimeLocal = () => toDatetimeLocal(new Date());

/** Lightweight URL validation for the optional call-to-action link. */
const isValidHttpUrl = (value) => {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
};

const AnnouncementsPage = () => {
    // State for database selection (prod or dev)
    const [useProdDb, setUseProdDb] = useState(true);
    
    // State for announcements list
    const [announcements, setAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // State for form
    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState('announcement');
    // Multi-surface display targets (e.g. ['banner', 'chat']). Replaces the old single displayMode.
    const [displaySurfaces, setDisplaySurfaces] = useState(['banner']);
    // Optional call-to-action link (primarily rendered by the chat strip surface).
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [isChangelog, setIsChangelog] = useState(false);
    // Audience targeting: 'all' broadcasts, 'organizations' limits to the picked orgs.
    const [audienceType, setAudienceType] = useState(AUDIENCE_ALL);
    const [audienceOrganizations, setAudienceOrganizations] = useState([]); // [{ id, name }]
    const [audienceIntegrations, setAudienceIntegrations] = useState([]); // integration IDs
    const [orgSearch, setOrgSearch] = useState('');
    const [orgSearchResults, setOrgSearchResults] = useState([]);
    const [isSearchingOrgs, setIsSearchingOrgs] = useState(false);
    const [orgSearchError, setOrgSearchError] = useState(null);
    const [date, setDate] = useState(nowDatetimeLocal()); // Start date + time
    const [endDate, setEndDate] = useState(''); // Optional end date + time for announcement visibility
    const [mediaFiles, setMediaFiles] = useState([]); // Array of { file: File, preview: string, isExisting: boolean, url?: string }
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeletingId, setIsDeletingId] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    
    // State for email sending
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedAnnouncementsForEmail, setSelectedAnnouncementsForEmail] = useState([]);
    const [clients, setClients] = useState([]);
    const [selectedClients, setSelectedClients] = useState([]);
    const [isLoadingClients, setIsLoadingClients] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailSubject, setEmailSubject] = useState('');
    const [searchClient, setSearchClient] = useState('');
    const [emailHtml, setEmailHtml] = useState('');
    
    // State for contact presets
    const [contactPresets, setContactPresets] = useState({});
    const [contactPresetCounts, setContactPresetCounts] = useState({}); // Counts for lazy loading
    const [loadingPresetCategory, setLoadingPresetCategory] = useState(null); // Currently loading category
    const [selectedPresets, setSelectedPresets] = useState([]);
    const [usePresets, setUsePresets] = useState(false);
    
    // Email settings from Marketing page
    const subscriptionGroups = [
        "product-updates", 
        "tips-and-tricks",
        "promotions", 
        "survey", 
        "customer-feedback", 
        "beta-invitations",
        "onboarding", 
        "important-account-updates",
        "announcement"
    ];
    const [selectedSubscriptionGroup, setSelectedSubscriptionGroup] = useState('announcement');
    
    const senders = [
        "\"Addy AI\" <info@addy-ai.com>", 
        "\"Michael Vandi\" <info@addy-ai.com>",
        "\"Michael Vandi\" <michael@addy-ai.com>"
    ];
    const [selectedSender, setSelectedSender] = useState(senders[0]);
    
    // Announcement selection for bulk send
    const [selectedAnnouncementIds, setSelectedAnnouncementIds] = useState([]);
    
    // Ref for TinyMCE editor
    const editorRef = useRef(null);
    
    // Derived state: Flat list of all unique users from selected presets
    const availableClients = useMemo(() => {
        if (!usePresets) {
            return clients; // Use old behavior when presets not enabled
        }
        
        const uniqueUsersMap = new Map();
        selectedPresets.forEach(presetKey => {
            const presetUsers = contactPresets[presetKey] || [];
            console.log(`[Preset] ${presetKey}: ${presetUsers.length} users`);
            presetUsers.forEach(user => {
                // Use EMAIL as the key for deduplication
                uniqueUsersMap.set(user.email, {
                    email: user.email,
                    name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email?.split('@')[0] || 'Unknown',
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                });
            });
        });
        const result = Array.from(uniqueUsersMap.values());
        console.log(`[availableClients] Total unique users from ${selectedPresets.length} presets: ${result.length}`);
        return result;
    }, [selectedPresets, contactPresets, clients, usePresets]);
    
    /**
     * Searches organizations by name prefix, or by exact ID when the term looks
     * like a document ID. Firestore string ranges are case-sensitive, so a few
     * common casings are tried and merged — the same approach the backend org
     * search uses.
     */
    const searchOrganizations = useCallback(async (term) => {
        const trimmed = term.trim();
        if (!trimmed) {
            setOrgSearchResults([]);
            setOrgSearchError(null);
            return;
        }

        setIsSearchingOrgs(true);
        setOrgSearchError(null);
        try {
            const db = useProdDb ? firestoreProd : firestoreDev;
            if (!db) {
                throw new Error('Firebase is not initialized');
            }

            const matches = new Map();

            // Exact ID first — org IDs are unique and admins often paste them.
            try {
                const directSnap = await getDoc(doc(db, 'organizations', trimmed));
                if (directSnap.exists()) {
                    matches.set(directSnap.id, {
                        id: directSnap.id,
                        name: orgDisplayName(directSnap.data(), directSnap.id)
                    });
                }
            } catch {
                // Not an ID-shaped string; fall through to the name search.
            }

            const variants = new Set([trimmed, trimmed.toLowerCase(), trimmed.toUpperCase()]);
            if (trimmed[0]) {
                variants.add(trimmed[0].toUpperCase() + trimmed.slice(1));
            }

            const snapshots = await Promise.all(
                [...variants].map((variant) =>
                    getDocs(query(
                        collection(db, 'organizations'),
                        orderBy('name'),
                        startAt(variant),
                        endAt(variant + '\uf8ff'),
                        limit(ORG_SEARCH_LIMIT)
                    )).catch(() => null)
                )
            );

            snapshots.forEach((snapshot) => {
                snapshot?.docs.forEach((orgDoc) => {
                    if (!matches.has(orgDoc.id)) {
                        matches.set(orgDoc.id, { id: orgDoc.id, name: orgDisplayName(orgDoc.data(), orgDoc.id) });
                    }
                });
            });

            const results = [...matches.values()]
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
                .slice(0, ORG_SEARCH_LIMIT);
            setOrgSearchResults(results);
        } catch (error) {
            console.error('Error searching organizations:', error);
            setOrgSearchResults([]);
            setOrgSearchError('Failed to search organizations');
        } finally {
            setIsSearchingOrgs(false);
        }
    }, [useProdDb]);

    // Debounce the org search so typing does not fire a query per keystroke.
    useEffect(() => {
        const timer = setTimeout(() => searchOrganizations(orgSearch), 300);
        return () => clearTimeout(timer);
    }, [orgSearch, searchOrganizations]);

    /** Adds an organization to the announcement's audience (no-op if already there). */
    const addAudienceOrganization = (organization) => {
        setAudienceOrganizations((prev) =>
            prev.some((org) => org.id === organization.id) ? prev : [...prev, organization]
        );
    };

    /** Removes an organization from the announcement's audience. */
    const removeAudienceOrganization = (organizationId) => {
        setAudienceOrganizations((prev) => prev.filter((org) => org.id !== organizationId));
    };

    /** Builds the `audience` object persisted on the announcement document. */
    const buildAudiencePayload = () => {
        if (audienceType === AUDIENCE_ORGANIZATIONS) {
            return {
                type: AUDIENCE_ORGANIZATIONS,
                organizationIds: audienceOrganizations.map((org) => org.id)
            };
        }
        if (audienceType === AUDIENCE_INTEGRATIONS) {
            return { type: AUDIENCE_INTEGRATIONS, integrations: audienceIntegrations };
        }
        return { type: AUDIENCE_ALL, organizationIds: [] };
    };

    /** Adds or removes an integration from the announcement's audience. */
    const toggleAudienceIntegration = (integrationId) => {
        setAudienceIntegrations((prev) =>
            prev.includes(integrationId)
                ? prev.filter((id) => id !== integrationId)
                : [...prev, integrationId]
        );
    };

    /**
     * Loads all announcements from Firestore
     * Announcements are ordered by date in descending order (newest first)
     */
    const loadAnnouncements = useCallback(async () => {
        setIsLoading(true);
        try {
            const db = useProdDb ? firestoreProd : firestoreDev;
            if (!db) {
                throw new Error('Firebase is not initialized');
            }
            
            const announcementsRef = collection(db, 'announcements');
            const q = query(announcementsRef, orderBy('date', 'desc'));
            const snapshot = await getDocs(q);
            
            const announcementsData = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    // Convert Firestore timestamp to a datetime-local string (date + time)
                    date: toDatetimeLocal(data.date) || data.date,
                    // Convert endDate if it exists
                    endDate: data.endDate ? (toDatetimeLocal(data.endDate) || data.endDate) : null
                };
            });
            
            setAnnouncements(announcementsData);
        } catch (error) {
            console.error('Error loading announcements:', error);
            setError('Failed to load announcements');
            toast.error('Failed to load announcements');
        } finally {
            setIsLoading(false);
        }
    }, [useProdDb]); // Reload when database selection changes
    
    // Load announcements on component mount
    useEffect(() => {
        loadAnnouncements();
    }, [loadAnnouncements]);

    const getYouTubeEmbedUrl = (url) => {
        try {
            const parsedUrl = new URL(url);
            const hostname = parsedUrl.hostname.replace(/^www\./, '');
            let videoId = '';

            if (hostname === 'youtu.be') {
                videoId = parsedUrl.pathname.split('/').filter(Boolean)[0] || '';
            } else if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
                if (parsedUrl.pathname.startsWith('/shorts/')) {
                    videoId = parsedUrl.pathname.split('/').filter(Boolean)[1] || '';
                } else if (parsedUrl.pathname.startsWith('/embed/')) {
                    videoId = parsedUrl.pathname.split('/').filter(Boolean)[1] || '';
                } else {
                    videoId = parsedUrl.searchParams.get('v') || '';
                }
            }

            return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
        } catch {
            return null;
        }
    };

    const isYouTubeUrl = (url) => Boolean(getYouTubeEmbedUrl(url));

    const handleAddYouTubeUrl = () => {
        const trimmedUrl = youtubeUrl.trim();
        if (!trimmedUrl) return;

        if (!isYouTubeUrl(trimmedUrl)) {
            toast.error('Please enter a valid YouTube URL');
            return;
        }

        if (mediaFiles.some(mediaItem => mediaItem.url === trimmedUrl)) {
            toast.error('This YouTube video is already attached');
            return;
        }

        setMediaFiles(prev => [
            ...prev,
            {
                url: trimmedUrl,
                preview: trimmedUrl,
                isExisting: true,
                isYoutube: true,
                file: null
            }
        ]);
        setYoutubeUrl('');
    };
    
    /**
     * Handles media files selection
     * Shows preview for images and validates file types
     * Supports multiple file uploads
     */
    const handleMediaChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm'];
        const maxSize = 10 * 1024 * 1024; // 10MB
        
        const newMediaFiles = [];
        
        files.forEach(file => {
            // Validate file type
            if (!validTypes.includes(file.type)) {
                toast.error(`Invalid file type for ${file.name}. Please upload images or videos only.`);
                return;
            }
            
            // Validate file size
            if (file.size > maxSize) {
                toast.error(`File ${file.name} exceeds 10MB. Please upload a smaller file.`);
                return;
            }
            
            // Create preview for images
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setMediaFiles(prev => {
                        const updated = [...prev];
                        const index = updated.findIndex(m => m.file === file);
                        if (index !== -1) {
                            updated[index].preview = reader.result;
                        }
                        return updated;
                    });
                };
                reader.readAsDataURL(file);
                newMediaFiles.push({ file, preview: null, isExisting: false });
            } else {
                // For videos, show video icon or filename
                newMediaFiles.push({ file, preview: null, isExisting: false });
            }
        });
        
        setMediaFiles(prev => [...prev, ...newMediaFiles]);
        
        // Reset input
        e.target.value = '';
    };
    
    /**
     * Removes a media file from the list
     */
    const handleRemoveMedia = (index) => {
        setMediaFiles(prev => prev.filter((_, i) => i !== index));
    };
    
    /**
     * Handles drag start for reordering
     */
    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };
    
    /**
     * Handles drag over
     */
    const handleDragOver = (e) => {
        e.preventDefault();
    };
    
    /**
     * Handles drop for reordering
     */
    const handleDrop = (dropIndex) => {
        if (draggedIndex === null || draggedIndex === dropIndex) return;
        
        setMediaFiles(prev => {
            const updated = [...prev];
            const [draggedItem] = updated.splice(draggedIndex, 1);
            updated.splice(dropIndex, 0, draggedItem);
            return updated;
        });
        
        setDraggedIndex(null);
    };
    
    /**
     * Uploads media file to Firebase Storage
     * Returns the signed download URL
     */
    const uploadMediaToFirebase = async (file) => {
        const storage = getStorage();
        const timestamp = Date.now();
        const randomSuffix = Math.random().toString(36).substring(2, 8);
        const fileName = `announcements/${timestamp}_${randomSuffix}_${file.name}`;
        const storageRef = ref(storage, fileName);
        
        try {
            // Upload file
            await uploadBytes(storageRef, file);
            
            // Get download URL
            const downloadURL = await getDownloadURL(storageRef);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading media:', error);
            throw new Error('Failed to upload media file');
        }
    };
    
    /**
     * Uploads multiple media files to Firebase Storage
     * Returns array of signed download URLs in order
     */
    const uploadMultipleMediaToFirebase = async (mediaArray) => {
        const uploadPromises = mediaArray.map(async (mediaItem) => {
            if (mediaItem.isExisting && mediaItem.url) {
                // Already uploaded, return existing URL
                return mediaItem.url;
            } else if (mediaItem.file) {
                // Upload new file
                return await uploadMediaToFirebase(mediaItem.file);
            }
            return null;
        });
        
        const urls = await Promise.all(uploadPromises);
        return urls.filter(url => url !== null);
    };
    
    /**
     * Handles form submission for creating or updating announcements
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        if (!title.trim()) {
            toast.error('Please enter a title');
            return;
        }
        
        if (!description.trim()) {
            toast.error('Please enter a description');
            return;
        }
        
        if (!date) {
            toast.error('Please select a start date');
            return;
        }
        
        // Validate end date is after start date if provided
        if (endDate && new Date(endDate) < new Date(date)) {
            toast.error('End date must be after or equal to the start date');
            return;
        }

        // Require at least one display surface
        if (!displaySurfaces || displaySurfaces.length === 0) {
            toast.error('Please select at least one display surface');
            return;
        }

        // A filtered announcement with nothing selected would reach nobody
        if (audienceType === AUDIENCE_ORGANIZATIONS && audienceOrganizations.length === 0) {
            toast.error('Please select at least one organization, or set the audience to all users');
            return;
        }
        if (audienceType === AUDIENCE_INTEGRATIONS && audienceIntegrations.length === 0) {
            toast.error('Please select at least one integration, or set the audience to all users');
            return;
        }

        // Validate the optional call-to-action link
        const trimmedLinkText = linkText.trim();
        const trimmedLinkUrl = linkUrl.trim();
        if (trimmedLinkUrl && !isValidHttpUrl(trimmedLinkUrl)) {
            toast.error('Please enter a valid link URL (http or https)');
            return;
        }
        if (trimmedLinkText && !trimmedLinkUrl) {
            toast.error('Please enter a link URL for the link text, or clear the link text');
            return;
        }
        
        // Make the audience unmissable before it goes live
        let audienceSummary = 'ALL users';
        if (audienceType === AUDIENCE_ORGANIZATIONS) {
            audienceSummary =
                `only ${audienceOrganizations.length} organization${audienceOrganizations.length === 1 ? '' : 's'}: ` +
                audienceOrganizations.map((org) => org.name).join(', ');
        } else if (audienceType === AUDIENCE_INTEGRATIONS) {
            audienceSummary =
                'only organizations connected to ' +
                audienceIntegrations.map(integrationDisplayName).join(', ');
        }
        if (!window.confirm(`This announcement will be shown to ${audienceSummary}.\n\nContinue?`)) {
            return;
        }

        setIsSubmitting(true);
        
        try {
            const pendingYoutubeUrl = youtubeUrl.trim();
            if (pendingYoutubeUrl) {
                if (!isYouTubeUrl(pendingYoutubeUrl)) {
                    toast.error('Please enter a valid YouTube URL');
                    setIsSubmitting(false);
                    return;
                }
                if (!mediaFiles.some(mediaItem => mediaItem.url === pendingYoutubeUrl)) {
                    setMediaFiles(prev => [
                        ...prev,
                        {
                            url: pendingYoutubeUrl,
                            preview: pendingYoutubeUrl,
                            isExisting: true,
                            isYoutube: true,
                            file: null
                        }
                    ]);
                }
            }

            // Upload all media files
            let mediaUrls = [];
            const mediaFilesForSubmit = pendingYoutubeUrl && !mediaFiles.some(mediaItem => mediaItem.url === pendingYoutubeUrl)
                ? [
                    ...mediaFiles,
                    {
                        url: pendingYoutubeUrl,
                        preview: pendingYoutubeUrl,
                        isExisting: true,
                        isYoutube: true,
                        file: null
                    }
                ]
                : mediaFiles;

            if (mediaFilesForSubmit.length > 0) {
                mediaUrls = await uploadMultipleMediaToFirebase(mediaFilesForSubmit);
            }
            
            const announcementData = {
                title: title.trim(),
                description: description.trim(), // This is already in Markdown format from TinyMCE
                type,
                displaySurfaces, // Canonical multi-surface list
                displayMode: toLegacyDisplayMode(displaySurfaces), // Legacy field for older clients
                linkText: trimmedLinkText || null,
                linkUrl: trimmedLinkUrl || null,
                isChangelog,
                audience: buildAudiencePayload(),
                date: new Date(date),
                endDate: endDate ? new Date(endDate) : null, // Optional end date for visibility window
                mediaUrls: mediaUrls, // Array of URLs
                updatedAt: serverTimestamp()
            };
            
            const db = useProdDb ? firestoreProd : firestoreDev;
            const dbLabel = useProdDb ? 'Production' : 'Development';
            
            if (isEditing && editingId) {
                // Update existing announcement
                const announcementRef = doc(db, 'announcements', editingId);
                await updateDoc(announcementRef, {
                    ...announcementData,
                    isImportant: deleteField()
                });
                toast.success(`Announcement updated successfully (${dbLabel})`);
            } else {
                // Create new announcement
                announcementData.createdAt = serverTimestamp();
                await addDoc(collection(db, 'announcements'), announcementData);
                toast.success(`Announcement created successfully (${dbLabel})`);
            }
            
            // Reset form
            resetForm();
            
            // Reload announcements
            await loadAnnouncements();
        } catch (error) {
            console.error('Error saving announcement:', error);
            toast.error('Failed to save announcement');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    /**
     * Resets the form to initial state
     */
    const resetForm = () => {
        setTitle('');
        setDescription('');
        setType('announcement');
        setDisplaySurfaces(['banner']);
        setLinkText('');
        setLinkUrl('');
        setIsChangelog(false);
        setAudienceType(AUDIENCE_ALL);
        setAudienceOrganizations([]);
        setAudienceIntegrations([]);
        setOrgSearch('');
        setOrgSearchResults([]);
        setDate(nowDatetimeLocal());
        setEndDate(''); // Reset end date
        setMediaFiles([]);
        setYoutubeUrl('');
        setIsEditing(false);
        setEditingId(null);
        setShowForm(false);
        setDraggedIndex(null);
        
        // Reset TinyMCE editor
        if (editorRef.current) {
            editorRef.current.setContent('');
        }
    };
    
    /**
     * Resolves display names for organization IDs stored on an announcement, so
     * editing shows "Acme Lending" rather than a raw document ID.
     */
    const loadAudienceOrganizationNames = async (organizationIds) => {
        try {
            const db = useProdDb ? firestoreProd : firestoreDev;
            if (!db) return;

            const snapshots = await Promise.all(
                organizationIds.map((id) =>
                    getDoc(doc(db, 'organizations', id)).catch(() => null)
                )
            );
            const namesById = new Map();
            snapshots.forEach((snapshot, index) => {
                const id = organizationIds[index];
                namesById.set(id, snapshot?.exists() ? orgDisplayName(snapshot.data(), id) : id);
            });
            setAudienceOrganizations((prev) =>
                prev.map((org) => ({ ...org, name: namesById.get(org.id) || org.name }))
            );
        } catch (error) {
            // Names are cosmetic — keep the IDs rather than blocking the edit.
            console.error('Error loading audience organization names:', error);
        }
    };

    /**
     * Handles editing an existing announcement
     * Populates the form with announcement data
     */
    const handleEdit = (announcement) => {
        setShowForm(true);
        setIsEditing(true);
        setEditingId(announcement.id);
        setTitle(announcement.title);
        setType(announcement.type);
        setDisplaySurfaces(resolveSurfacesFromAnnouncement(announcement));
        setLinkText(announcement.linkText || '');
        setLinkUrl(announcement.linkUrl || '');
        setIsChangelog(Boolean(announcement.isChangelog));
        const audience = resolveAudienceFromAnnouncement(announcement);
        setAudienceType(audience.type);
        setAudienceIntegrations(audience.integrations);
        setOrgSearch('');
        setOrgSearchResults([]);
        // Show the IDs immediately, then swap in names once they resolve.
        setAudienceOrganizations(audience.organizationIds.map((id) => ({ id, name: id })));
        if (audience.organizationIds.length > 0) {
            loadAudienceOrganizationNames(audience.organizationIds);
        }
        setDate(announcement.date);
        // Set end date if it exists
        setEndDate(announcement.endDate || '');
        
        // Load existing media URLs
        if (announcement.mediaUrls && Array.isArray(announcement.mediaUrls)) {
            const existingMedia = announcement.mediaUrls.map(url => ({
                url,
                preview: url,
                isExisting: true,
                file: null
            }));
            setMediaFiles(existingMedia);
        } else if (announcement.mediaUrl) {
            // Support old single mediaUrl format
            setMediaFiles([{
                url: announcement.mediaUrl,
                preview: announcement.mediaUrl,
                isExisting: true,
                file: null
            }]);
        } else {
            setMediaFiles([]);
        }
        
        // Set description and update editor content
        setDescription(announcement.description);
        
        // Update TinyMCE editor content after a brief delay to ensure editor is mounted
        setTimeout(() => {
            if (editorRef.current) {
                editorRef.current.setContent(announcement.description || '');
            }
        }, 100);
        
        // Scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    
    /**
     * Handles deleting an announcement
     */
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this announcement?')) {
            return;
        }
        
        setIsDeletingId(id);
        
        try {
            const db = useProdDb ? firestoreProd : firestoreDev;
            const dbLabel = useProdDb ? 'Production' : 'Development';
            await deleteDoc(doc(db, 'announcements', id));
            toast.success(`Announcement deleted successfully (${dbLabel})`);
            await loadAnnouncements();
        } catch (error) {
            console.error('Error deleting announcement:', error);
            toast.error('Failed to delete announcement');
        } finally {
            setIsDeletingId(null);
        }
    };
    
    /**
     * Handles canceling edit mode
     */
    const handleCancelEdit = () => {
        resetForm();
    };
    
    /**
     * Loads all clients from Firestore (legacy method, kept for backwards compatibility)
     */
    const loadClients = useCallback(async () => {
        setIsLoadingClients(true);
        try {
            const db = useProdDb ? firestoreProd : firestoreDev;
            if (!db) {
                throw new Error('Firebase is not initialized');
            }
            
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('createdAccountOnClient', '==', true));
            const snapshot = await getDocs(q);
            
            const clientsData = snapshot.docs.map(doc => ({
                email: doc.data().email,
                name: doc.data().name || doc.data().email?.split('@')[0] || 'Unknown',
                createdAt: doc.data().createdAt
            })).filter(client => client.email); // Filter out users without email
            
            // Sort by email
            clientsData.sort((a, b) => a.email.localeCompare(b.email));
            
            setClients(clientsData);
        } catch (error) {
            console.error('Error loading clients:', error);
            toast.error('Failed to load clients');
        } finally {
            setIsLoadingClients(false);
        }
    }, [useProdDb]);
    
    /**
     * Loads contact preset counts from backend (fast initial load)
     * Only loads the category names and user counts, not the actual user data
     */
    const loadContactPresets = useCallback(async () => {
        setIsLoadingClients(true);
        try {
            // First, load just the counts (fast endpoint)
            const response = await fetch(`${apiUrl}/admin/contacts/presets`);
            const data = await response.json();
            
            if (!data.success) {
                throw new Error('Failed to load contact preset counts');
            }
            
            // Store the counts for display
            setContactPresetCounts(data.data);
            
            // Initialize empty presets - users will be loaded on demand
            const emptyPresets = {};
            Object.keys(data.data).forEach(key => {
                emptyPresets[key] = []; // Empty array, will be populated when selected
            });
            setContactPresets(emptyPresets);
            
            console.log('[loadContactPresets] Loaded contact preset counts:', data.data);
        } catch (error) {
            console.error('Error loading contact presets:', error);
            toast.error('Failed to load contact presets. Falling back to individual selection.');
            // Fallback to old method
            setUsePresets(false);
            await loadClients();
        } finally {
            setIsLoadingClients(false);
        }
    }, [loadClients]);

    /**
     * Normalizes users from preset API, removes invalid/archived entries and deduplicates by email
     */
    const normalizePresetUsers = useCallback((users = []) => {
        const deduplicatedUsers = new Map();

        users.forEach((user) => {
            const normalizedEmail = (user?.email || '').trim().toLowerCase();
            if (!normalizedEmail) return;

            // Guard against archived/inactive flags if backend starts returning them
            const isArchivedOrInactive =
                user?.archived === true ||
                user?.isArchived === true ||
                user?.deleted === true ||
                user?.isDeleted === true ||
                user?.disabled === true ||
                user?.isDisabled === true ||
                user?.active === false ||
                user?.isActive === false ||
                user?.status === 'archived' ||
                user?.status === 'inactive' ||
                user?.status === 'deleted';

            if (isArchivedOrInactive) return;

            deduplicatedUsers.set(normalizedEmail, {
                email: normalizedEmail,
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                phone: user?.phone || '',
                name:
                    user?.name ||
                    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
                    normalizedEmail.split('@')[0] ||
                    'Unknown',
            });
        });

        return Array.from(deduplicatedUsers.values()).sort((a, b) => a.email.localeCompare(b.email));
    }, []);

    /**
     * Loads users for a specific preset category (lazy load on demand)
     * @param {string} category - The preset category to load
     */
    const loadPresetUsers = useCallback(async (category) => {
        if (contactPresets[category]?.length > 0) {
            // Already loaded
            console.log(`[loadPresetUsers] Category ${category} already loaded with ${contactPresets[category].length} users`);
            return contactPresets[category];
        }
        
        setLoadingPresetCategory(category);
        try {
            const PAGE_LIMIT = 500;
            const MAX_PAGES = 40; // safety cap to avoid infinite loops
            let page = 0;
            let offset = 0;
            let hasMore = true;
            let allUsers = [];
            let backendTotal = 0;

            while (hasMore && page < MAX_PAGES) {
                const response = await fetch(
                    `${apiUrl}/admin/contacts/preset/${encodeURIComponent(category)}?limit=${PAGE_LIMIT}&offset=${offset}`
                );
                const data = await response.json();

                if (!data.success) {
                    throw new Error(`Failed to load users for ${category}`);
                }

                const pageUsers = Array.isArray(data.data) ? data.data : [];
                allUsers = allUsers.concat(pageUsers);
                backendTotal = typeof data.total === 'number' ? data.total : backendTotal;

                if (!data.hasMore || pageUsers.length === 0) {
                    hasMore = false;
                } else {
                    offset += PAGE_LIMIT;
                    page += 1;
                }
            }

            const users = normalizePresetUsers(allUsers);
            
            // Update the presets with the loaded users
            setContactPresets(prev => ({
                ...prev,
                [category]: users
            }));
            
            // Update the count with actual count from response
            setContactPresetCounts(prev => ({
                ...prev,
                [category]: users.length
            }));

            if (hasMore) {
                toast.warning(
                    `Loaded first ${users.length} recipients for "${category}". Please narrow the list if needed.`
                );
            }
            
            console.log(`[loadPresetUsers] Loaded ${users.length} users for category ${category} (backend total: ${backendTotal})`);
            return users;
        } catch (error) {
            console.error(`Error loading users for preset ${category}:`, error);
            toast.error(`Failed to load users for ${category}`);
            return [];
        } finally {
            setLoadingPresetCategory(null);
        }
    }, [contactPresets, normalizePresetUsers]);
    
    /**
     * Opens email modal for specific announcement(s)
     */
    const handleSendEmailClick = (announcement = null) => {
        // If announcement is provided, send single announcement
        // Otherwise, send selected announcements
        let announcementsToEmail = [];
        
        if (announcement) {
            announcementsToEmail = [announcement];
            setSelectedAnnouncementsForEmail([announcement]);
            setEmailSubject(announcement.title);
        } else if (selectedAnnouncementIds.length > 0) {
            const selectedAnnouncements = announcements.filter(a => 
                selectedAnnouncementIds.includes(a.id)
            );
            announcementsToEmail = selectedAnnouncements;
            setSelectedAnnouncementsForEmail(selectedAnnouncements);
            setEmailSubject(
                selectedAnnouncements.length === 1 
                    ? selectedAnnouncements[0].title 
                    : `${selectedAnnouncements.length} Announcements`
            );
        } else {
            toast.error('Please select at least one announcement');
            return;
        }
        
        setShowEmailModal(true);
        setSelectedClients([]);
        setSelectedPresets([]);
        setSearchClient('');
        
        // Generate initial email HTML with branding
        const generatedHtml = generateEmailHTML(announcementsToEmail);
        setEmailHtml(generatedHtml);
        
        // Load contact presets if not already loaded
        if (Object.keys(contactPresetCounts).length === 0) {
            setUsePresets(true);
            loadContactPresets();
        }
    };
    
    /**
     * Toggles announcement selection for bulk operations
     */
    const toggleAnnouncementSelection = (announcementId) => {
        setSelectedAnnouncementIds(prev => {
            if (prev.includes(announcementId)) {
                return prev.filter(id => id !== announcementId);
            } else {
                return [...prev, announcementId];
            }
        });
    };
    
    /**
     * Selects or deselects all announcements
     */
    const toggleSelectAllAnnouncements = () => {
        if (selectedAnnouncementIds.length === announcements.length) {
            setSelectedAnnouncementIds([]);
        } else {
            setSelectedAnnouncementIds(announcements.map(a => a.id));
        }
    };
    
    /**
     * Toggles client selection (by email)
     */
    const toggleClientSelection = (clientEmail) => {
        console.log('[toggleClientSelection] Clicked email:', clientEmail);
        console.log('[toggleClientSelection] Current selectedClients:', selectedClients);
        console.log('[toggleClientSelection] Is selected?:', selectedClients.includes(clientEmail));
        
        setSelectedClients(prev => {
            const isCurrentlySelected = prev.includes(clientEmail);
            let newSelection;
            
            if (isCurrentlySelected) {
                newSelection = prev.filter(email => email !== clientEmail);
                console.log('[toggleClientSelection] DESELECTING - New count:', newSelection.length);
            } else {
                newSelection = [...prev, clientEmail];
                console.log('[toggleClientSelection] SELECTING - New count:', newSelection.length);
            }
            
            return newSelection;
        });
    };
    
    /**
     * Selects or deselects all clients (using emails)
     */
    const toggleSelectAll = () => {
        const filteredClientEmails = filteredClients.map(c => c.email);
        const allSelected = filteredClientEmails.length > 0 && 
                           filteredClientEmails.every(email => selectedClients.includes(email));
        
        if (allSelected) {
            // Deselect all filtered clients
            setSelectedClients(prev => prev.filter(email => !filteredClientEmails.includes(email)));
        } else {
            // Select all filtered clients
            setSelectedClients(prev => {
                const uniqueEmails = new Set([...prev, ...filteredClientEmails]);
                return Array.from(uniqueEmails);
            });
        }
    };
    
    /**
     * Toggles a preset selection (using emails)
     * Loads users on demand if not already loaded
     */
    const togglePreset = async (presetKey) => {
        console.log(`[togglePreset] Toggling preset: ${presetKey}`);
        
        const isCurrentlySelected = selectedPresets.includes(presetKey);
        
        if (isCurrentlySelected) {
            // DESELECTING: Remove all users from this preset from selectedClients
            const presetUsers = contactPresets[presetKey] || [];
            const emailsToRemove = new Set(presetUsers.map(u => u.email));
            console.log(`[togglePreset] Removing ${emailsToRemove.size} users`);
            setSelectedClients(emails => {
                const filtered = emails.filter(email => !emailsToRemove.has(email));
                console.log(`[togglePreset] Selected clients after removal: ${filtered.length}`);
                return filtered;
            });
            setSelectedPresets(prev => prev.filter(p => p !== presetKey));
        } else {
            // SELECTING: Load users if not already loaded, then add to selectedClients
            let presetUsers = contactPresets[presetKey] || [];
            
            // If users not loaded yet, load them first
            if (presetUsers.length === 0 && contactPresetCounts[presetKey] > 0) {
                console.log(`[togglePreset] Loading users for ${presetKey}...`);
                presetUsers = await loadPresetUsers(presetKey);
            }
            
            console.log(`[togglePreset] Preset has ${presetUsers.length} users`);
            
            // Log first 3 user emails
            if (presetUsers.length > 0) {
                console.log(`[togglePreset] Sample emails from preset:`, presetUsers.slice(0, 3).map(u => u.email));
            }
            
            const emailsToAdd = presetUsers.map(u => u.email);
            console.log(`[togglePreset] Adding ${emailsToAdd.length} users`);
            setSelectedClients(emails => {
                const uniqueEmails = new Set([...emails, ...emailsToAdd]);
                const result = Array.from(uniqueEmails);
                console.log(`[togglePreset] Selected clients after addition: ${result.length}`);
                return result;
            });
            setSelectedPresets(prev => [...prev, presetKey]);
        }
    };
    
    /**
     * Select all presets (using emails)
     * Loads all preset users on demand
     */
    const selectAllPresets = async () => {
        const allPresetKeys = Object.keys(contactPresetCounts).filter(key => key !== 'everybody');
        setSelectedPresets(allPresetKeys);
        
        // Load all preset users in parallel
        const loadPromises = allPresetKeys.map(async (presetKey) => {
            if (contactPresets[presetKey]?.length === 0 && contactPresetCounts[presetKey] > 0) {
                return loadPresetUsers(presetKey);
            }
            return contactPresets[presetKey] || [];
        });
        
        const allPresetUsers = await Promise.all(loadPromises);
        
        // Add all users from all presets (by email)
        const allEmails = new Set();
        allPresetUsers.forEach(users => {
            users.forEach(user => {
                allEmails.add(user.email);
            });
        });
        setSelectedClients(Array.from(allEmails));
    };
    
    /**
     * Clear all presets
     */
    const clearAllPresets = () => {
        setSelectedPresets([]);
        setSelectedClients([]);
    };
    
    /**
     * Format preset name for display
     */
    const formatPresetName = (presetKey) => {
        // Integration presets come back from the backend as "integration-<id>";
        // show the catalog name rather than a title-cased slug.
        if (presetKey.startsWith(INTEGRATION_PRESET_PREFIX)) {
            const integrationId = presetKey.slice(INTEGRATION_PRESET_PREFIX.length);
            return `${integrationDisplayName(integrationId)} users`;
        }
        return presetKey
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };
    
    /**
     * Check if a preset is fully/partially selected (using emails)
     */
    const getPresetSelectionState = (presetKey) => {
        const presetEmails = (contactPresets[presetKey] || []).map(u => u.email);
        if (presetEmails.length === 0) {
            return { checked: false, indeterminate: false };
        }
        
        const selectedCount = presetEmails.filter(email => selectedClients.includes(email)).length;
        const allSelected = selectedCount === presetEmails.length;
        const someSelected = selectedCount > 0 && !allSelected;
        
        return {
            checked: allSelected,
            indeterminate: someSelected
        };
    };
    
    /**
     * Generates email HTML from announcement(s) with branding
     */
    const generateEmailHTML = (announcements) => {
        let contentHtml = '';
        
        // Add intro message
        contentHtml += '<div style="font-family: Arial, sans-serif; padding: 20px;">';
        contentHtml += '<h2 style="color: #444a5b; font-size: 24px; margin-bottom: 10px;">Latest Updates from Addy AI</h2>';
        contentHtml += '<p style="color: #666; font-size: 16px; margin-bottom: 30px;">Here are the latest announcements and updates from our team.</p>';
        contentHtml += '</div>';
        
        // Handle multiple announcements
        announcements.forEach((announcement, idx) => {
            if (idx > 0) {
                contentHtml += '<hr style="margin: 40px 20px; border: none; border-top: 2px solid #e0e0e0;" />';
            }
            
            contentHtml += '<div style="font-family: Arial, sans-serif; padding: 0 20px;">';
            
            // Title and badge in same row
            contentHtml += '<div style="display: flex; align-items: center; margin-bottom: 15px;">';
            contentHtml += `<h1 style="color: #333; margin: 0; margin-right: 10px; font-size: 28px;">${announcement.title}</h1>`;
            const badgeColor = announcement.type === 'announcement' ? '#0d6efd' : '#198754';
            const badgeText = announcement.type === 'announcement' ? 'Announcement' : 'Fix';
            contentHtml += `<span style="background-color: ${badgeColor}; color: white; padding: 4px 12px; border-radius: 4px; font-size: 12px; font-weight: 600;">${badgeText}</span>`;
            contentHtml += '</div>';
            
            // Description
            contentHtml += `<div style="color: #555; line-height: 1.6; margin-bottom: 20px; font-size: 16px;">${announcement.description}</div>`;
            
            // Add media if available
            if (announcement.mediaUrls && announcement.mediaUrls.length > 0) {
                contentHtml += '<div style="margin: 20px 0;">';
                announcement.mediaUrls.forEach((url, index) => {
                    if (url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
                        contentHtml += `<img src="${url}" alt="Media ${index + 1}" style="max-width: 100%; height: auto; margin-bottom: 10px; border-radius: 8px; display: block;" />`;
                    } else {
                        // For non-image media, show a link
                        contentHtml += `<p style="margin: 10px 0;"><a href="${url}" style="color: #0d6efd; text-decoration: none;">📎 View attachment ${index + 1}</a></p>`;
                    }
                });
                contentHtml += '</div>';
            }
            
            // Add date
            contentHtml += `<p style="color: #999; font-size: 14px; margin-top: 20px;">Published: ${announcement.date}</p>`;
            contentHtml += '</div>';
        });
        
        // Wrap with branding
        const fullHtml = `<div style="padding: 20px;">${defaultBrandedHeader}${contentHtml}${defaultBrandedFooter}</div>`;
        return fullHtml;
    };
    
    /**
     * Converts HTML to plain text
     */
    const htmlToText = (html) => {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    };
    
    /**
     * Sends email to selected clients using the same endpoint as Marketing page
     */
    const handleSendEmail = async () => {
        if (selectedAnnouncementsForEmail.length === 0) {
            toast.error('No announcement selected');
            return;
        }
        
        if (selectedClients.length === 0) {
            toast.error('Please select at least one client');
            return;
        }
        
        if (!emailSubject.trim()) {
            toast.error('Please enter an email subject');
            return;
        }
        
        setIsSendingEmail(true);
        
        try {
            // Use the edited HTML from GrapesJS editor
            const emailHTML = emailHtml;
            const emailPlainText = htmlToText(emailHTML);
            
            // Get selected client data (works with both presets and individual selection)
            const selectedClientsData = (usePresets ? availableClients : clients).filter(client => selectedClients.includes(client.email));
            
            console.log('[handleSendEmail] Sending to', selectedClientsData.length, 'clients');
            
            // Prepare email details array (same format as Marketing page)
            const emailDetails = selectedClientsData.map(client => ({
                from: selectedSender,
                to: client.email,
                subject: emailSubject,
                emailHTML: emailHTML.replaceAll("{{uid}}", client.email), // Use email for unsubscribe link
                emailPlainText: emailPlainText,
                emailKind: selectedSubscriptionGroup,
            }));
            
            // Send emails using the same endpoint as Marketing page
            await sendHTMLEmailViaSendGrid(emailDetails);
            
            toast.success(`Email sent successfully to ${selectedClients.length} recipient${selectedClients.length > 1 ? 's' : ''}!`);
            setShowEmailModal(false);
            setSelectedAnnouncementsForEmail([]);
            setSelectedClients([]);
            setSelectedAnnouncementIds([]);
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error('Failed to send email: ' + error.message);
        } finally {
            setIsSendingEmail(false);
        }
    };
    
    /**
     * Sends HTML emails via SendGrid (same as Marketing page)
     */
    const sendHTMLEmailViaSendGrid = async (emails) => {
        try {
            const payload = {
                token: ADMIN_TOKEN,
                emails,
            };
            
            console.log('Sending email payload:', { 
                endpoint: `${cloudRunApiUrl}/admin/send-email-batch`,
                tokenPresent: !!payload.token,
                emailCount: emails.length 
            });
            
            const requestOptions = {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            };

            const ENDPOINT = `${cloudRunApiUrl}/admin/send-email-batch`;

            const response = await fetch(ENDPOINT, requestOptions);
            const reader = response.body.getReader();

            if (response.status !== 200) {
                console.error(`Failed to send email. Status: ${response.status}`);
                throw new Error(`Failed to send emails. Status: ${response.status}`);
            }

            let successCount = 0;
            let errorCount = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                const chunkString = new TextDecoder().decode(value);
                try {
                    const responseData = JSON.parse(chunkString);
                    if (responseData?.success) {
                        successCount++;
                        console.log("Successfully sent email to:", responseData.email?.to);
                    } else {
                        errorCount++;
                        console.log("Failed to send email:", responseData);
                    }
                } catch (e) {
                    console.error("Error parsing response chunk:", chunkString);
                }
            }

            if (errorCount > 0 && successCount === 0) {
                throw new Error(`Failed to send all emails. ${errorCount} failed.`);
            }

            return { success: true, successCount, errorCount };
        } catch (error) {
            console.error('Error sending email:', error);
            throw error;
        }
    };
    
    // Filter clients based on search (use availableClients when presets are enabled)
    const filteredClients = useMemo(() => {
        const sourceClients = usePresets ? availableClients : clients;
        console.log(`[filteredClients] Source: ${usePresets ? 'availableClients' : 'clients'}, Count: ${sourceClients.length}`);
        const filtered = sourceClients.filter(client => 
            client.email.toLowerCase().includes(searchClient.toLowerCase()) ||
            client.name.toLowerCase().includes(searchClient.toLowerCase())
        );
        console.log(`[filteredClients] After search filter: ${filtered.length}`);
        return filtered;
    }, [usePresets, availableClients, clients, searchClient]);
    
    // Loading state
    if (isLoading && announcements.length === 0) {
        return (
            <div className="container-fluid p-4">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }
    
    // Error state
    if (error && announcements.length === 0) {
        return (
            <div className="container-fluid p-4">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }
    
    return (
        <div className="container-fluid p-4 AnnouncementsPageContainer" style={{ maxWidth: 'auto' }}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                    <h1 className="mb-0">Announcements Management</h1>
                    {/* Database Toggle Switch */}
                    <div className="d-flex align-items-center gap-2 ms-3">
                        <span 
                            className={`badge ${!useProdDb ? 'bg-warning text-dark' : 'bg-secondary'}`}
                            style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                            onClick={() => setUseProdDb(false)}
                        >
                            DEV
                        </span>
                        <div className="form-check form-switch mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="dbSwitch"
                                checked={useProdDb}
                                onChange={(e) => setUseProdDb(e.target.checked)}
                                style={{ width: '3rem', height: '1.5rem', cursor: 'pointer' }}
                            />
                        </div>
                        <span 
                            className={`badge ${useProdDb ? 'bg-success' : 'bg-secondary'}`}
                            style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                            onClick={() => setUseProdDb(true)}
                        >
                            PROD
                        </span>
                    </div>
                </div>
                <div className="d-flex gap-2">
                    {selectedAnnouncementIds.length > 0 && (
                        <button
                            className="btn btn-success"
                            onClick={() => handleSendEmailClick()}
                        >
                            📧 Send {selectedAnnouncementIds.length} Selected ({selectedAnnouncementIds.length})
                        </button>
                    )}
                    {!showForm && (
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                setShowForm(true);
                                // Clear editor content when creating new
                                setTimeout(() => {
                                    if (editorRef.current) {
                                        editorRef.current.setContent('');
                                    }
                                }, 100);
                            }}
                        >
                            + Create New Announcement
                        </button>
                    )}
                </div>
            </div>
            
            {/* Create/Edit Announcement Form */}
            {showForm && (
                <div className="card mb-4">
                    <div className="card-header">
                        <h2 className="h5 mb-0">
                            {isEditing ? 'Edit Announcement' : 'Create New Announcement'}
                        </h2>
                    </div>
                    <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        {/* Title */}
                        <div className="mb-3">
                            <label htmlFor="title" className="form-label">
                                Title <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                id="title"
                                className="form-control"
                                placeholder="Enter announcement title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                        
                        {/* Description (Rich Text Editor) */}
                        <div className="mb-3">
                            <label htmlFor="description" className="form-label">
                                Description <span className="text-danger">*</span>
                            </label>
                            <TinyMCEEditor
                                onInit={(evt, editor) => {
                                    editorRef.current = editor;
                                    // Set initial content if we have description
                                    if (description) {
                                        editor.setContent(description);
                                    }
                                }}
                                initialValue=""
                                onEditorChange={(content, editor) => {
                                    // Only update state, don't trigger re-render of editor
                                    setDescription(content);
                                }}
                                init={{
                                    height: 400,
                                    menubar: false,
                                    plugins: [
                                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                        'insertdatetime', 'media', 'table', 'help', 'wordcount'
                                    ],
                                    toolbar: 'undo redo | blocks | ' +
                                        'bold italic forecolor | alignleft aligncenter ' +
                                        'alignright alignjustify | bullist numlist outdent indent | ' +
                                        'removeformat | link | code | help',
                                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                    // Export as Markdown-compatible HTML
                                    entity_encoding: 'raw',
                                    forced_root_block: 'p',
                                    paste_as_text: false,
                                    setup: (editor) => {
                                        // Prevent unnecessary updates
                                        editor.on('init', () => {
                                            editor.setContent(description || '');
                                        });
                                    }
                                }}
                                disabled={isSubmitting}
                            />
                            <small className="form-text text-muted">
                                Content will be saved in Markdown format
                            </small>
                        </div>
                        
                        <div className="row">
                            {/* Type */}
                            <div className="col-md-3 mb-3">
                                <label htmlFor="type" className="form-label">
                                    Type <span className="text-danger">*</span>
                                </label>
                                <select
                                    id="type"
                                    className="form-select"
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    disabled={isSubmitting}
                                    required
                                >
                                    <option value="announcement">Announcement</option>
                                    <option value="fix">Fix</option>
                                </select>
                            </div>

                            {/* Display Surfaces (multi-select) */}
                            <div className="col-md-3 mb-3">
                                <label className="form-label">
                                    Display Surfaces <span className="text-danger">*</span>
                                </label>
                                <div className="border rounded p-2">
                                    {DISPLAY_SURFACE_OPTIONS.map((option) => {
                                        const checked = displaySurfaces.includes(option.value);
                                        return (
                                            <div className="form-check" key={option.value}>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`surface-${option.value}`}
                                                    checked={checked}
                                                    disabled={isSubmitting}
                                                    onChange={(e) => {
                                                        setDisplaySurfaces((prev) =>
                                                            e.target.checked
                                                                ? [...prev, option.value]
                                                                : prev.filter((s) => s !== option.value)
                                                        );
                                                    }}
                                                />
                                                <label className="form-check-label" htmlFor={`surface-${option.value}`} title={option.help}>
                                                    {option.label}
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                                <small className="form-text text-muted">
                                    Pick one or more places to show this announcement.
                                </small>
                            </div>

                            {/* Is Changelog */}
                            <div className="col-md-3 mb-3">
                                <label htmlFor="isChangelog" className="form-label">
                                    Is Changelog
                                </label>
                                <select
                                    id="isChangelog"
                                    className="form-select"
                                    value={isChangelog ? 'true' : 'false'}
                                    onChange={(e) => setIsChangelog(e.target.value === 'true')}
                                    disabled={isSubmitting}
                                >
                                    <option value="false">False</option>
                                    <option value="true">True</option>
                                </select>
                            </div>
                            
                            {/* Start Date & Time */}
                            <div className="col-md-3 mb-3">
                                <label htmlFor="date" className="form-label">
                                    Start Date &amp; Time <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    id="date"
                                    className="form-control"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    disabled={isSubmitting}
                                    required
                                />
                                <small className="form-text text-muted">
                                    When the announcement becomes visible
                                </small>
                            </div>
                        </div>
                        
                        <div className="row">
                            {/* End Date & Time */}
                            <div className="col-md-6 mb-3">
                                <label htmlFor="endDate" className="form-label">
                                    End Date &amp; Time (Optional)
                                </label>
                                <input
                                    type="datetime-local"
                                    id="endDate"
                                    className="form-control"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={date} // End date must be >= start date
                                    disabled={isSubmitting}
                                />
                                <small className="form-text text-muted">
                                    When the announcement stops being visible. Leave empty for no expiration (will use 2-week default).
                                </small>
                            </div>
                        </div>

                        {/* Call-to-action link (used by the chat strip) */}
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label htmlFor="linkText" className="form-label">
                                    Link Text (Optional)
                                </label>
                                <input
                                    type="text"
                                    id="linkText"
                                    className="form-control"
                                    placeholder="e.g. Learn more"
                                    value={linkText}
                                    maxLength={LINK_TEXT_MAX}
                                    onChange={(e) => setLinkText(e.target.value)}
                                    disabled={isSubmitting}
                                />
                                <small className="form-text text-muted">
                                    {linkText.length}/{LINK_TEXT_MAX} characters
                                </small>
                            </div>
                            <div className="col-md-8 mb-3">
                                <label htmlFor="linkUrl" className="form-label">
                                    Link URL (Optional)
                                </label>
                                <input
                                    type="url"
                                    id="linkUrl"
                                    className="form-control"
                                    placeholder="https://help.addy.so/..."
                                    value={linkUrl}
                                    onChange={(e) => setLinkUrl(e.target.value)}
                                    disabled={isSubmitting}
                                />
                                <small className="form-text text-muted">
                                    Opens in a new tab. Shown on the Chat strip surface.
                                </small>
                            </div>
                        </div>

                        {/* Audience targeting */}
                        <div className="mb-3">
                            <label className="form-label">
                                Audience <span className="text-danger">*</span>
                            </label>
                            <div className="border rounded p-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="audienceType"
                                        id="audience-all"
                                        checked={audienceType === AUDIENCE_ALL}
                                        disabled={isSubmitting}
                                        onChange={() => setAudienceType(AUDIENCE_ALL)}
                                    />
                                    <label className="form-check-label" htmlFor="audience-all">
                                        All users
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="audienceType"
                                        id="audience-organizations"
                                        checked={audienceType === AUDIENCE_ORGANIZATIONS}
                                        disabled={isSubmitting}
                                        onChange={() => setAudienceType(AUDIENCE_ORGANIZATIONS)}
                                    />
                                    <label className="form-check-label" htmlFor="audience-organizations">
                                        Specific organizations
                                    </label>
                                </div>

                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="audienceType"
                                        id="audience-integrations"
                                        checked={audienceType === AUDIENCE_INTEGRATIONS}
                                        disabled={isSubmitting}
                                        onChange={() => setAudienceType(AUDIENCE_INTEGRATIONS)}
                                    />
                                    <label className="form-check-label" htmlFor="audience-integrations">
                                        Organizations using an integration
                                    </label>
                                </div>

                                {audienceType === AUDIENCE_INTEGRATIONS && (
                                    <div className="mt-3">
                                        <div className="row">
                                            {INTEGRATION_OPTIONS.map((option) => (
                                                <div className="col-md-4" key={option.id}>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id={`integration-${option.id}`}
                                                            checked={audienceIntegrations.includes(option.id)}
                                                            disabled={isSubmitting}
                                                            onChange={() => toggleAudienceIntegration(option.id)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`integration-${option.id}`}>
                                                            {option.name}
                                                            <small className="text-muted d-block">{option.type}</small>
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="mt-3">
                                            {audienceIntegrations.length === 0 ? (
                                                <span className="text-danger">
                                                    No integrations selected — this announcement would reach nobody.
                                                </span>
                                            ) : (
                                                <strong>
                                                    Will be shown to every organization connected to{' '}
                                                    {audienceIntegrations.map(integrationDisplayName).join(', ')}.
                                                </strong>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {audienceType === AUDIENCE_ORGANIZATIONS && (
                                    <div className="mt-3">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search organizations by name, or paste an organization ID"
                                            value={orgSearch}
                                            onChange={(e) => setOrgSearch(e.target.value)}
                                            disabled={isSubmitting}
                                        />

                                        {isSearchingOrgs && (
                                            <small className="form-text text-muted">Searching…</small>
                                        )}
                                        {orgSearchError && (
                                            <small className="form-text text-danger">{orgSearchError}</small>
                                        )}
                                        {!isSearchingOrgs && !orgSearchError && orgSearch.trim() && orgSearchResults.length === 0 && (
                                            <small className="form-text text-muted">
                                                No organizations match &quot;{orgSearch.trim()}&quot;. Name search is prefix-based and case-sensitive; pasting the exact organization ID always works.
                                            </small>
                                        )}

                                        {orgSearchResults.length > 0 && (
                                            <div className="list-group mt-2" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                                {orgSearchResults.map((org) => {
                                                    const alreadySelected = audienceOrganizations.some((selected) => selected.id === org.id);
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={org.id}
                                                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                            disabled={isSubmitting || alreadySelected}
                                                            onClick={() => addAudienceOrganization(org)}
                                                        >
                                                            <span>
                                                                {org.name}
                                                                <small className="text-muted d-block">{org.id}</small>
                                                            </span>
                                                            {alreadySelected && <span className="badge bg-secondary">Selected</span>}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        <div className="mt-3">
                                            {audienceOrganizations.length === 0 ? (
                                                <span className="text-danger">
                                                    No organizations selected — this announcement would reach nobody.
                                                </span>
                                            ) : (
                                                <>
                                                    <div className="mb-2">
                                                        <strong>
                                                            Will be shown to {audienceOrganizations.length} organization
                                                            {audienceOrganizations.length === 1 ? '' : 's'}:
                                                        </strong>
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {audienceOrganizations.map((org) => (
                                                            <span key={org.id} className="badge bg-primary d-flex align-items-center gap-2" title={org.id}>
                                                                {org.name}
                                                                <button
                                                                    type="button"
                                                                    className="btn-close btn-close-white"
                                                                    style={{ fontSize: '0.6rem' }}
                                                                    aria-label={`Remove ${org.name}`}
                                                                    disabled={isSubmitting}
                                                                    onClick={() => removeAudienceOrganization(org.id)}
                                                                />
                                                            </span>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <small className="form-text text-muted">
                                {audienceType === AUDIENCE_ALL &&
                                    'Everyone who uses Addy will see this announcement.'}
                                {audienceType === AUDIENCE_ORGANIZATIONS &&
                                    'Only users who belong to the selected organizations will see this announcement in the app.'}
                                {audienceType === AUDIENCE_INTEGRATIONS &&
                                    'Matched live at view time: any organization with one of these integrations connected, including ones that connect after this is published. Requested, rejected or disabled connections do not count.'}
                            </small>
                        </div>

                        {/* Chat strip live preview */}
                        {displaySurfaces.includes('chat') && (
                            <div className="mb-3">
                                <label className="form-label">Chat strip preview</label>
                                <div
                                    className="d-flex align-items-center justify-content-between"
                                    style={{
                                        gap: '12px',
                                        padding: '10px 14px',
                                        border: '1px solid #eff1f6',
                                        borderRadius: '12px',
                                        background: '#fff',
                                        boxShadow: '0 1px 10px rgba(226, 229, 236, 0.4)',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            color: '#2d2f39',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {title || 'Your announcement title'}
                                    </span>
                                    <span className="d-flex align-items-center" style={{ gap: '12px', flexShrink: 0 }}>
                                        {linkText && linkUrl && (
                                            <span style={{ color: '#8740ff', fontSize: '14px', fontWeight: 600, textDecoration: 'underline' }}>
                                                {linkText}
                                            </span>
                                        )}
                                        <span style={{ color: '#6e717c', fontSize: '18px', lineHeight: 1 }}>×</span>
                                    </span>
                                </div>
                                {title.length > CHAT_TITLE_MAX && (
                                    <small className="form-text text-warning">
                                        Title is {title.length} characters; chat strips read best under {CHAT_TITLE_MAX}.
                                    </small>
                                )}
                            </div>
                        )}
                        
                        {/* Media Upload */}
                        <div className="mb-3">
                            <label htmlFor="media" className="form-label">
                                Media Upload (Optional)
                            </label>
                            <input
                                type="file"
                                id="media"
                                className="form-control"
                                accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
                                onChange={handleMediaChange}
                                disabled={isSubmitting}
                                multiple
                            />
                            <small className="form-text text-muted">
                                Supported formats: JPEG, PNG, GIF, WEBP, MP4, WEBM (Max 10MB each). You can upload multiple files.
                            </small>

                            <div className="mt-3">
                                <label htmlFor="youtubeUrl" className="form-label">
                                    YouTube Video URL (Optional)
                                </label>
                                <div className="input-group">
                                    <input
                                        type="url"
                                        id="youtubeUrl"
                                        className="form-control"
                                        placeholder="https://www.youtube.com/watch?v=..."
                                        value={youtubeUrl}
                                        onChange={(e) => setYoutubeUrl(e.target.value)}
                                        disabled={isSubmitting}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={handleAddYouTubeUrl}
                                        disabled={isSubmitting || !youtubeUrl.trim()}
                                    >
                                        Add Video
                                    </button>
                                </div>
                                <small className="form-text text-muted">
                                    YouTube, youtu.be, Shorts, and embed links are supported.
                                </small>
                            </div>
                            
                            {/* Media Preview Grid */}
                            {mediaFiles.length > 0 && (
                                <div className="mt-3">
                                    <p className="mb-2"><strong>Media Files ({mediaFiles.length}):</strong></p>
                                    <small className="text-muted d-block mb-2">
                                        Drag and drop to reorder
                                    </small>
                                    <div className="d-flex flex-wrap gap-3">
                                        {mediaFiles.map((mediaItem, index) => {
                                            const isYoutube = Boolean(mediaItem.url && isYouTubeUrl(mediaItem.url));
                                            const isImage = mediaItem.preview && (
                                                mediaItem.preview.startsWith('data:image') || 
                                                mediaItem.preview.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                                            );
                                            
                                            return (
                                                <div
                                                    key={index}
                                                    draggable
                                                    onDragStart={() => handleDragStart(index)}
                                                    onDragOver={handleDragOver}
                                                    onDrop={() => handleDrop(index)}
                                                    className={`position-relative border rounded p-2 ${draggedIndex === index ? 'opacity-50' : ''}`}
                                                    style={{ 
                                                        width: '150px', 
                                                        cursor: 'move',
                                                        backgroundColor: draggedIndex === index ? '#f0f0f0' : 'white'
                                                    }}
                                                >
                                                    {/* Order Badge */}
                                                    <div 
                                                        className="position-absolute top-0 start-0 badge bg-primary m-1"
                                                        style={{ zIndex: 1 }}
                                                    >
                                                        #{index + 1}
                                                    </div>
                                                    
                                                    {/* Remove Button */}
                                                    <button
                                                        type="button"
                                                        className="position-absolute top-0 end-0 btn btn-sm btn-danger m-1"
                                                        style={{ zIndex: 1, padding: '2px 6px', fontSize: '12px' }}
                                                        onClick={() => handleRemoveMedia(index)}
                                                        disabled={isSubmitting}
                                                    >
                                                        ×
                                                    </button>
                                                    
                                                    {/* Preview */}
                                                    {isImage ? (
                                                        <img 
                                                            src={mediaItem.preview} 
                                                            alt={`Media ${index + 1}`}
                                                            className="w-100 rounded"
                                                            style={{ height: '120px', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div 
                                                            className="d-flex align-items-center justify-content-center bg-light rounded"
                                                            style={{ height: '120px' }}
                                                        >
                                                            {isYoutube ? (
                                                                <div className="text-center p-2">
                                                                    <div style={{ fontSize: '24px' }}>▶</div>
                                                                    <small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }}>
                                                                        YouTube video
                                                                    </small>
                                                                </div>
                                                            ) : mediaItem.file ? (
                                                                <div className="text-center p-2">
                                                                    <div style={{ fontSize: '24px' }}>🎬</div>
                                                                    <small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }}>
                                                                        {mediaItem.file.name}
                                                                    </small>
                                                                </div>
                                                            ) : (
                                                                <a 
                                                                    href={mediaItem.url} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="text-decoration-none"
                                                                >
                                                                    <div className="text-center">
                                                                        <div style={{ fontSize: '24px' }}>📎</div>
                                                                        <small>View File</small>
                                                                    </div>
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Form Actions */}
                        <div className="d-flex gap-2">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <span>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {isEditing ? 'Updating...' : 'Creating...'}
                                    </span>
                                ) : (
                                    isEditing ? 'Update Announcement' : 'Create Announcement'
                                )}
                            </button>
                            
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancelEdit}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            )}
            
            {/* Announcements Table */}
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h2 className="h5 mb-0">All Announcements</h2>
                    {announcements.length > 0 && (
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={toggleSelectAllAnnouncements}
                        >
                            {selectedAnnouncementIds.length === announcements.length ? 'Deselect All' : 'Select All'}
                        </button>
                    )}
                </div>
                <div className="card-body">
                    {announcements.length === 0 ? (
                        <div className="alert alert-info">
                            No announcements found. Create your first announcement above.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th style={{ width: '5%' }}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={selectedAnnouncementIds.length === announcements.length}
                                                onChange={toggleSelectAllAnnouncements}
                                            />
                                        </th>
                                        <th style={{ width: '18%' }}>Title</th>
                                        <th style={{ width: '8%' }}>Type</th>
                                        <th style={{ width: '12%' }}>Format</th>
                                        <th style={{ width: '12%' }}>Audience</th>
                                        <th style={{ width: '10%' }}>Start Date</th>
                                        <th style={{ width: '10%' }}>End Date</th>
                                        <th style={{ width: '8%' }}>Media</th>
                                        <th style={{ width: '17%' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {announcements.map((announcement) => (
                                        <tr key={announcement.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedAnnouncementIds.includes(announcement.id)}
                                                    onChange={() => toggleAnnouncementSelection(announcement.id)}
                                                />
                                            </td>
                                            <td>
                                                <strong>{announcement.title}</strong>
                                            </td>
                                            <td>
                                                <span className={`badge ${announcement.type === 'announcement' ? 'bg-primary' : 'bg-success'}`}>
                                                    {announcement.type === 'announcement' ? 'Announcement' : 'Fix'}
                                                </span>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const surfaces = resolveSurfacesFromAnnouncement(announcement);
                                                    const badgeClassFor = (surface) =>
                                                        surface === 'modal' ? 'bg-warning text-dark'
                                                            : surface === 'chat' ? 'bg-info text-dark'
                                                            : 'bg-secondary';
                                                    const labelFor = (surface) =>
                                                        DISPLAY_SURFACE_OPTIONS.find((o) => o.value === surface)?.label || surface;
                                                    return (
                                                        <span className="d-flex flex-wrap gap-1">
                                                            {surfaces.map((surface) => (
                                                                <span key={surface} className={`badge ${badgeClassFor(surface)}`}>
                                                                    {labelFor(surface)}
                                                                </span>
                                                            ))}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td>
                                                {(() => {
                                                    const audience = resolveAudienceFromAnnouncement(announcement);
                                                    if (audience.type === AUDIENCE_INTEGRATIONS) {
                                                        return (
                                                            <span className="d-flex flex-wrap gap-1">
                                                                {audience.integrations.map((id) => (
                                                                    <span key={id} className="badge bg-dark">
                                                                        {integrationDisplayName(id)}
                                                                    </span>
                                                                ))}
                                                            </span>
                                                        );
                                                    }
                                                    if (audience.type === AUDIENCE_ORGANIZATIONS) {
                                                        return (
                                                            <span
                                                                className="badge bg-dark"
                                                                title={audience.organizationIds.join('\n')}
                                                            >
                                                                {audience.organizationIds.length} org
                                                                {audience.organizationIds.length === 1 ? '' : 's'}
                                                            </span>
                                                        );
                                                    }
                                                    return <span className="badge bg-light text-dark">All users</span>;
                                                })()}
                                            </td>
                                            <td>{announcement.date}</td>
                                            <td>
                                                {announcement.endDate ? (
                                                    <span>{announcement.endDate}</span>
                                                ) : (
                                                    <span className="text-muted" title="Uses 2-week default expiration">—</span>
                                                )}
                                            </td>
                                            <td>
                                                {announcement.mediaUrls && announcement.mediaUrls.length > 0 ? (
                                                    <span>
                                                        📎 {announcement.mediaUrls.length} file{announcement.mediaUrls.length > 1 ? 's' : ''}
                                                    </span>
                                                ) : announcement.mediaUrl ? (
                                                    <span>📎 1 file</span>
                                                ) : (
                                                    <span className="text-muted">No media</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    <button
                                                        className="btn btn-sm btn-outline-success"
                                                        onClick={() => handleSendEmailClick(announcement)}
                                                        disabled={isSubmitting || isDeletingId !== null}
                                                        title="Send email to clients"
                                                    >
                                                        📧 Send Email
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => handleEdit(announcement)}
                                                        disabled={isSubmitting || isDeletingId !== null}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDelete(announcement.id)}
                                                        disabled={isSubmitting || isDeletingId !== null}
                                                    >
                                                        {isDeletingId === announcement.id ? (
                                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                        ) : (
                                                            'Delete'
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Email Sending Modal */}
            <style>
                {`
                    .modal-90w {
                        max-width: 90%;
                    }
                    .modal-90w .modal-body {
                        max-height: 80vh;
                        overflow-y: auto;
                    }
                `}
            </style>
            <Modal 
                show={showEmailModal} 
                onHide={() => setShowEmailModal(false)}
                size="xl"
                dialogClassName="modal-90w"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Send Announcement Email</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedAnnouncementsForEmail.length > 0 && (
                        <>
                            {/* Email Content Editor */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0">
                                        {selectedAnnouncementsForEmail.length === 1 
                                            ? 'Edit Email Content' 
                                            : `Edit Email Content (${selectedAnnouncementsForEmail.length} Announcements)`
                                        }
                                    </h5>
                                    <span className="badge bg-info">
                                        Includes branding, logo & footer
                                    </span>
                                </div>
                                <GrapesEditor
                                    key={selectedAnnouncementsForEmail.map(a => a.id).join('-')}
                                    initialContent={emailHtml}
                                    onChange={(html) => setEmailHtml(html)}
                                />
                            </div>
                            
                            {/* Email Settings */}
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <label className="form-label">
                                        <strong>Subscription Group</strong>
                                    </label>
                                    <select
                                        className="form-select"
                                        value={selectedSubscriptionGroup}
                                        onChange={(e) => setSelectedSubscriptionGroup(e.target.value)}
                                    >
                                        {subscriptionGroups.map((group) => (
                                            <option key={group} value={group}>
                                                {group}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">
                                        <strong>Sender</strong>
                                    </label>
                                    <select
                                        className="form-select"
                                        value={selectedSender}
                                        onChange={(e) => setSelectedSender(e.target.value)}
                                    >
                                        {senders.map((sender) => (
                                            <option key={sender} value={sender}>
                                                {sender}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            {/* Email Subject */}
                            <div className="mb-4">
                                <label htmlFor="emailSubject" className="form-label">
                                    <strong>Email Subject</strong>
                                </label>
                                <input
                                    type="text"
                                    id="emailSubject"
                                    className="form-control"
                                    value={emailSubject}
                                    onChange={(e) => setEmailSubject(e.target.value)}
                                    placeholder="Enter email subject"
                                />
                            </div>
                            
                            {/* Selection Mode Toggle */}
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Recipients</h5>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                        setUsePresets(!usePresets);
                                        setSelectedClients([]);
                                        setSelectedPresets([]);
                                        if (!usePresets && Object.keys(contactPresetCounts).length === 0) {
                                            loadContactPresets();
                                        } else if (usePresets && clients.length === 0) {
                                            loadClients();
                                        }
                                    }}
                                    disabled={isLoadingClients}
                                >
                                    {usePresets ? 'Switch to Individual Selection' : 'Switch to Group Selection'}
                                </button>
                            </div>
                            
                            {/* Preset Selection */}
                            {usePresets && Object.keys(contactPresetCounts).length > 0 && (
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0">Select Recipient Groups</h6>
                                        <div className="d-flex gap-2">
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={selectAllPresets}
                                                disabled={isLoadingClients || loadingPresetCategory}
                                            >
                                                Select All
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={clearAllPresets}
                                                disabled={isLoadingClients || loadingPresetCategory}
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row g-2">
                                        {Object.entries(contactPresetCounts)
                                            .filter(([key]) => key !== 'everybody') // Hide dangerous preset
                                            .map(([presetKey, count]) => {
                                                const isSelected = selectedPresets.includes(presetKey);
                                                const isLoading = loadingPresetCategory === presetKey;
                                                const { indeterminate } = getPresetSelectionState(presetKey);
                                                return (
                                                    <div key={presetKey} className="col-md-6">
                                                        <div 
                                                            className={`form-check border rounded p-3 ${isLoading ? 'bg-light' : ''}`} 
                                                            style={{ cursor: isLoading ? 'wait' : 'pointer' }} 
                                                            onClick={() => !isLoading && togglePreset(presetKey)}
                                                        >
                                                            {isLoading ? (
                                                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                                                    <span className="visually-hidden">Loading...</span>
                                                                </div>
                                                            ) : (
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id={`preset-${presetKey}`}
                                                                    checked={isSelected}
                                                                    ref={el => {
                                                                        if (el) el.indeterminate = !isSelected && indeterminate;
                                                                    }}
                                                                    onChange={() => {}} // Handled by parent onClick
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    disabled={count === 0}
                                                                />
                                                            )}
                                                            <label 
                                                                className="form-check-label w-100" 
                                                                htmlFor={`preset-${presetKey}`}
                                                                style={{ cursor: isLoading ? 'wait' : 'pointer' }}
                                                            >
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="fw-semibold">{formatPresetName(presetKey)}</span>
                                                                    <span className={`badge ${isLoading ? 'bg-primary' : 'bg-secondary'}`}>
                                                                        {isLoading ? 'Loading...' : `~${count}`}
                                                                    </span>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                    {selectedPresets.length > 0 && (
                                        <div className="mt-3">
                                            <small className="text-muted">
                                                <strong>Selected groups ({selectedPresets.length}):</strong> {selectedPresets.map(formatPresetName).join(', ')}
                                            </small>
                                        </div>
                                    )}
                                    {selectedPresets.length === 0 && (
                                        <div className="alert alert-info mt-3 mb-0">
                                            <small>
                                                💡 Select one or more groups above to add recipients, then fine-tune your selection below.
                                            </small>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Client Selection */}
                            <div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">
                                        {usePresets ? 'Fine-tune Recipients' : 'Send to'} <span className="badge bg-primary">{selectedClients.length}</span> recipient(s)
                                        {usePresets && availableClients.length > 0 && (
                                            <small className="text-muted ms-2">
                                                (from {availableClients.length} in selected groups)
                                            </small>
                                        )}
                                        {selectedClients.length > 0 && (
                                            <span className="ms-2" style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                                                {(() => {
                                                    const sourceClients = usePresets ? availableClients : clients;
                                                    const selectedClientsData = sourceClients.filter(c => selectedClients.includes(c.email));
                                                    const displayClients = selectedClientsData.slice(0, 3);
                                                    const remaining = selectedClientsData.length - 3;
                                                    const remainingNames = selectedClientsData.slice(3).map(c => `${c.name} (${c.email})`).join('\n');
                                                    
                                                    return (
                                                        <>
                                                            {displayClients.map((client, idx) => (
                                                                <span key={client.id}>
                                                                    {idx > 0 && ', '}
                                                                    {client.name}
                                                                </span>
                                                            ))}
                                                            {remaining > 0 && (
                                                                <span 
                                                                    className="text-primary text-decoration-underline"
                                                                    style={{ cursor: 'help' }}
                                                                    title={remainingNames}
                                                                    data-bs-toggle="tooltip"
                                                                    data-bs-placement="bottom"
                                                                    data-bs-html="true"
                                                                >
                                                                    {' '}+{remaining} more
                                                                </span>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </span>
                                        )}
                                    </h6>
                                    <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={toggleSelectAll}
                                        disabled={isLoadingClients || filteredClients.length === 0}
                                    >
                                        {(() => {
                                            const filteredEmails = filteredClients.map(c => c.email);
                                            const allSelected = filteredEmails.length > 0 && 
                                                              filteredEmails.every(email => selectedClients.includes(email));
                                            return allSelected ? 'Deselect All' : 'Select All';
                                        })()}
                                    </button>
                                </div>
                                
                                {/* Search */}
                                <input
                                    type="text"
                                    className="form-control mb-3"
                                    placeholder="Search by email or name..."
                                    value={searchClient}
                                    onChange={(e) => setSearchClient(e.target.value)}
                                    disabled={isLoadingClients}
                                />
                                
                                {/* Clients Table */}
                                {isLoadingClients ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading clients...</span>
                                        </div>
                                        <p className="mt-2">Loading {usePresets ? 'contact groups' : 'clients'}...</p>
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }} className="border rounded">
                                        <table className="table table-hover mb-0">
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: '50px' }}>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={(() => {
                                                                const filteredEmails = filteredClients.map(c => c.email);
                                                                return filteredEmails.length > 0 && 
                                                                       filteredEmails.every(email => selectedClients.includes(email));
                                                            })()}
                                                            onChange={toggleSelectAll}
                                                        />
                                                    </th>
                                                    <th>Name</th>
                                                    <th>Email</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredClients.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="3" className="text-center text-muted py-3">
                                                            {usePresets && selectedPresets.length === 0 ? (
                                                                'Select one or more groups above to see recipients'
                                                            ) : searchClient ? (
                                                                'No clients found matching your search'
                                                            ) : (
                                                                'No clients available'
                                                            )}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    filteredClients.map((client, idx) => {
                                                        // Find which presets this client belongs to (only from selected presets)
                                                        const clientPresets = usePresets ? selectedPresets.filter(presetKey => 
                                                            contactPresets[presetKey]?.some(u => u.email === client.email)
                                                        ) : [];
                                                        
                                                        const isSelected = selectedClients.includes(client.email);
                                                        
                                                        // Log first 3 clients for debugging
                                                        if (idx < 3) {
                                                            console.log(`[Client ${idx}] Email: "${client.email}", Selected: ${isSelected}`);
                                                        }
                                                        
                                                        return (
                                                            <tr 
                                                                key={client.email}
                                                                onClick={() => toggleClientSelection(client.email)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <td>
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input"
                                                                        checked={isSelected}
                                                                        onChange={() => toggleClientSelection(client.email)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <div>{client.name}</div>
                                                                    {usePresets && clientPresets.length > 0 && (
                                                                        <div className="mt-1">
                                                                            {clientPresets.slice(0, 2).map(preset => (
                                                                                <span 
                                                                                    key={preset} 
                                                                                    className="badge bg-light text-dark me-1"
                                                                                    style={{ fontSize: '0.7rem' }}
                                                                                >
                                                                                    {formatPresetName(preset)}
                                                                                </span>
                                                                            ))}
                                                                            {clientPresets.length > 2 && (
                                                                                <span 
                                                                                    className="badge bg-light text-dark"
                                                                                    style={{ fontSize: '0.7rem' }}
                                                                                    title={clientPresets.slice(2).map(formatPresetName).join(', ')}
                                                                                >
                                                                                    +{clientPresets.length - 2}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>{client.email}</td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        {/* Recipients Preview on Footer */}
                        <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                            {selectedClients.length > 0 && (
                                <>
                                    Sending to: {(() => {
                                        const sourceClients = usePresets ? availableClients : clients;
                                        const selectedClientsData = sourceClients.filter(c => selectedClients.includes(c.email));
                                        const displayClients = selectedClientsData.slice(0, 2);
                                        const remaining = selectedClientsData.length - 2;
                                        const remainingList = selectedClientsData.slice(2).map(c => `${c.name} (${c.email})`).join('\n');
                                        
                                        return (
                                            <>
                                                {displayClients.map((client, idx) => (
                                                    <strong key={client.id}>
                                                        {idx > 0 && ', '}
                                                        {client.name}
                                                    </strong>
                                                ))}
                                                {remaining > 0 && (
                                                    <span className="position-relative">
                                                        <strong 
                                                            className="text-primary text-decoration-underline"
                                                            style={{ cursor: 'help' }}
                                                            onMouseEnter={(e) => {
                                                                const tooltip = e.currentTarget.nextElementSibling;
                                                                if (tooltip) tooltip.style.display = 'block';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                const tooltip = e.currentTarget.nextElementSibling;
                                                                if (tooltip) tooltip.style.display = 'none';
                                                            }}
                                                        >
                                                            {' '}and {remaining} other{remaining > 1 ? 's' : ''}
                                                        </strong>
                                                        <div 
                                                            className="position-absolute bg-dark text-white p-2 rounded shadow"
                                                            style={{
                                                                display: 'none',
                                                                bottom: '100%',
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                                marginBottom: '5px',
                                                                zIndex: 1050,
                                                                minWidth: '200px',
                                                                fontSize: '0.85rem',
                                                                whiteSpace: 'pre-line',
                                                                maxHeight: '300px',
                                                                overflowY: 'auto'
                                                            }}
                                                        >
                                                            {remainingList}
                                                            <div 
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: '-5px',
                                                                    left: '50%',
                                                                    transform: 'translateX(-50%)',
                                                                    width: 0,
                                                                    height: 0,
                                                                    borderLeft: '5px solid transparent',
                                                                    borderRight: '5px solid transparent',
                                                                    borderTop: '5px solid #212529'
                                                                }}
                                                            />
                                                        </div>
                                                    </span>
                                                )}
                                            </>
                                        );
                                    })()}
                                </>
                            )}
                        </div>
                        
                        <div className="d-flex gap-2">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowEmailModal(false)}
                                disabled={isSendingEmail}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-primary" 
                                onClick={handleSendEmail}
                                disabled={isSendingEmail || selectedClients.length === 0 || !emailSubject.trim()}
                            >
                                {isSendingEmail ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Sending...
                                    </>
                                ) : (
                                    `Send to ${selectedClients.length} recipient${selectedClients.length !== 1 ? 's' : ''}`
                                )}
                            </button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AnnouncementsPage;
